import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, styled, Typography } from '@mui/material';
import { SelectField } from 'components/Ui-V3/InputFields';
import { EColors, getColor } from 'theme';

const GridContainer = styled(Box)(({ theme: { spacing, breakpoints } }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing(1),
    marginTop: spacing(3),
    width: '100%',
    boxSizing: 'border-box',
    [breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
    },
}));

const GridItem = styled(Box, {
    shouldForwardProp: prop => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme: { spacing }, isSelected = false }) => ({
    minHeight: '63px',
    boxSizing: 'border-box',
    borderRadius: '12px',
    border: '0.827px solid ' + (isSelected ? getColor(EColors.v3Dark, 0.6) : getColor(EColors.v3Border)),
    background: getColor(EColors.v3Background),
    padding: spacing(1),
}));

const MainDataContainer = styled(Box)(({ theme: { spacing } }) => ({
    minHeight: '63px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    cursor: 'pointer',
}));

const IconContainer = styled(Box)({
    width: 45,
    height: 45,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    background: getColor(EColors.v3Background),
});

type FbNeedValuesType = { option: number; diningStyle?: number | null }[];

type SelectFieldProps = {
    label?: string;
    error?: string;
    values?: FbNeedValuesType;
    disabled?: boolean;
    required?: boolean;
    options: Bizly.FbOptionsType[];
    onChange?: (values: FbNeedValuesType) => void;
};

export const FoodBeverageSelector = ({ label, values, options, error, onChange }: SelectFieldProps) => {
    const onItemClick = (option: Bizly.FbOptionsType, isSelected: boolean) => {
        let newValues = values || [];
        if (isSelected) {
            newValues = newValues?.filter(item => item.option !== option.id);
        } else {
            newValues = [
                ...newValues,
                {
                    option: option.id,
                    diningStyle: option.diningStyles.length > 0 ? option.diningStyles[0].id : undefined,
                },
            ];
        }
        onChange?.(newValues);
    };

    const onDiningStyleChanged = (option: Bizly.FbOptionsType, diningStyleId: number) => {
        if (!values) return;

        const newValues = values.map(item => {
            if (item.option !== option.id) return item;

            return {
                ...item,
                diningStyle: diningStyleId,
            };
        });

        onChange?.(newValues);
    };

    return (
        <Box>
            {label && <Typography fontWeight={700}>{label}</Typography>}
            <GridContainer>
                {options?.map(option => {
                    const selectedValue = values?.find(v => v.option === option.id);
                    const isSelected = Boolean(selectedValue);
                    return (
                        <GridItem key={option.id} isSelected={isSelected}>
                            <MainDataContainer onClick={() => onItemClick(option, isSelected)}>
                                <IconContainer>
                                    <img src={option.iconUrl} width={16} height={16} />
                                </IconContainer>

                                <Typography flex={1} variant="body2" fontWeight={500}>
                                    {option.name}
                                </Typography>

                                {isSelected && <RadioButtonCheckedIcon sx={{ color: getColor(EColors.v3Dark, 0.6) }} />}
                                {!isSelected && (
                                    <RadioButtonUncheckedIcon sx={{ color: getColor(EColors.v3Dark, 0.3) }} />
                                )}
                            </MainDataContainer>
                            {isSelected && option.diningStyles.length > 0 && (
                                <SelectField
                                    value={selectedValue?.diningStyle || undefined}
                                    label="Dining Style"
                                    options={option.diningStyles.map(d => ({ value: d.id, label: d.name })) || []}
                                    onChange={dsValue => {
                                        onDiningStyleChanged(option, dsValue as number);
                                    }}
                                />
                            )}
                        </GridItem>
                    );
                })}
            </GridContainer>
            {error && (
                <Typography variant="body2" color="red">
                    {error}
                </Typography>
            )}
        </Box>
    );
};
